import type { AnalyticsClient, AnalyticsTransport } from "./analytics-client";
import { normalizeAnalyticsPath, sanitizeAnalyticsCapture } from "./events";
import { containsPostHogEmail } from "./posthog-property-safety";
import type { RuntimeConfig } from "../runtime-config";

type PostHogProperties = Readonly<Record<string, unknown>>;

export type PostHogEnvelope = Readonly<{
  event: string;
  properties: PostHogProperties;
}>;

export type PostHogSdkBoundary = Readonly<{
  init: (token: string, config: typeof POSTHOG_CONFIG) => unknown;
  capture: (event: string, properties: PostHogProperties) => unknown;
}>;

const DROPPED_SDK_PROPERTY_KEYS = [
  "$lib",
  "$lib_version",
  "$browser",
  "$device_type",
] as const;

const PROJECT_TOKEN_PATTERN = /^phc_[a-z0-9]{20,80}$/i;
const POSTHOG_COOKIELESS_DISTINCT_ID = "$posthog_cookieless";
const POSTHOG_COOKIELESS_HOST = "craniologia.me";

const asRecord = (value: unknown): PostHogProperties | null =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as PostHogProperties)
    : null;

const containsEmail = (value: unknown): boolean => {
  if (typeof value === "string") return containsPostHogEmail(value);
  if (Array.isArray(value)) return value.some(containsEmail);
  if (value !== null && typeof value === "object")
    return Object.values(value).some(containsEmail);
  return false;
};

const normalizeReferrer = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  try {
    return new URL(value).hostname.toLowerCase() || null;
  } catch {
    return null;
  }
};

const normalizeCookielessHost = (value: unknown): string | null => {
  if (typeof value !== "string" || value.length === 0 || value.length > 253)
    return null;
  try {
    const url = new URL(`https://${value}`);
    return url.host === value.toLowerCase() && url.pathname === "/"
      ? POSTHOG_COOKIELESS_HOST
      : null;
  } catch {
    return null;
  }
};

const containsControlCharacter = (value: string): boolean =>
  [...value].some((character) => {
    const code = character.charCodeAt(0);
    return code <= 31 || code === 127;
  });

const normalizeCookielessUserAgent = (value: unknown): string | null => {
  if (
    typeof value !== "string" ||
    value.length === 0 ||
    value.length > 1000 ||
    containsControlCharacter(value) ||
    containsPostHogEmail(value)
  )
    return null;

  const browser = (() => {
    const candidates = [
      ["edge", /(?:Edg|EdgiOS|EdgA)\/(\d{1,3})/i],
      ["firefox", /(?:Firefox|FxiOS)\/(\d{1,3})/i],
      ["chrome", /(?:Chrome|CriOS)\/(\d{1,3})/i],
      ["safari", /Version\/(\d{1,3}).*Safari\//i],
    ] as const;
    for (const [family, pattern] of candidates) {
      const match = pattern.exec(value);
      if (match?.[1]) return `${family}-${match[1]}`;
    }
    return "other-0";
  })();
  const os = /Android/i.test(value)
    ? "android"
    : /(?:iPhone|iPad|iPod)/i.test(value)
      ? "ios"
      : /Windows/i.test(value)
        ? "windows"
        : /Macintosh|Mac OS X/i.test(value)
          ? "macos"
          : /Linux/i.test(value)
            ? "linux"
            : "other";
  const device = /iPad|Tablet/i.test(value)
    ? "tablet"
    : /Mobile|iPhone|iPod|Android/i.test(value)
      ? "mobile"
      : "desktop";
  return `pdc/${browser}/${os}/${device}`;
};

const isPublicProjectToken = (value: unknown): value is string =>
  typeof value === "string" && PROJECT_TOKEN_PATTERN.test(value);

export const sanitizePostHogEnvelope = (
  envelope: unknown,
): PostHogEnvelope | null => {
  const source = asRecord(envelope);
  const sourceProperties = source && asRecord(source.properties);
  if (!source || typeof source.event !== "string" || !sourceProperties)
    return null;
  if (
    !isPublicProjectToken(sourceProperties.token) ||
    sourceProperties.distinct_id !== POSTHOG_COOKIELESS_DISTINCT_ID ||
    sourceProperties.$cookieless_mode !== true
  )
    return null;

  const productProperties = Object.fromEntries(
    Object.entries(sourceProperties).filter(
      ([key]) =>
        ![
          "token",
          "distinct_id",
          "$cookieless_mode",
          "$current_url",
          "$referrer",
          "$host",
          "$process_person_profile",
          "$raw_user_agent",
          ...DROPPED_SDK_PROPERTY_KEYS,
        ].includes(key),
    ),
  );
  if (containsEmail(productProperties)) return null;

  const capture = sanitizeAnalyticsCapture(source.event, productProperties);
  if (capture === null) return null;

  const properties: Record<string, unknown> = {
    ...capture.properties,
    token: sourceProperties.token,
    distinct_id: POSTHOG_COOKIELESS_DISTINCT_ID,
    $cookieless_mode: true,
  };
  if (sourceProperties.$current_url !== undefined) {
    if (typeof sourceProperties.$current_url !== "string") return null;
    const path = normalizeAnalyticsPath(sourceProperties.$current_url);
    if (path === null) return null;
    properties.$current_url = path;
  }

  if (sourceProperties.$referrer !== undefined) {
    const referrer = normalizeReferrer(sourceProperties.$referrer);
    if (referrer !== null) properties.$referrer = referrer;
  }

  if (sourceProperties.$host !== undefined) {
    const host = normalizeCookielessHost(sourceProperties.$host);
    if (host === null) return null;
    properties.$host = host;
  }

  if (sourceProperties.$raw_user_agent !== undefined) {
    const userAgent = normalizeCookielessUserAgent(
      sourceProperties.$raw_user_agent,
    );
    if (userAgent === null) return null;
    properties.$raw_user_agent = userAgent;
  }

  if (sourceProperties.$process_person_profile !== undefined) {
    if (sourceProperties.$process_person_profile !== false) return null;
    properties.$process_person_profile = false;
  }

  return { event: capture.event, properties };
};

export const POSTHOG_CONFIG = Object.freeze({
  api_host: "https://eu.i.posthog.com",
  defaults: "2026-05-30",
  cookieless_mode: "always",
  person_profiles: "never",
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  capture_dead_clicks: false,
  rageclick: false,
  capture_exceptions: false,
  capture_heatmaps: false,
  capture_performance: false,
  disable_session_recording: true,
  disable_surveys: true,
  respect_dnt: true,
  disable_compression: true,
  advanced_disable_flags: true,
  before_send: sanitizePostHogEnvelope,
} as const);

export const defaultPostHogLoader = async (): Promise<PostHogSdkBoundary> => {
  const { default: posthog } = await import("posthog-js");
  return posthog as unknown as PostHogSdkBoundary;
};

type InitializePostHogAnalyticsOptions = Readonly<{
  analytics: AnalyticsClient;
  runtimeConfig: RuntimeConfig;
  mode: string;
  isOptedOut: () => boolean;
  isDoNotTrack: () => boolean;
  loadSdk?: () => Promise<PostHogSdkBoundary>;
}>;

export const initializePostHogAnalytics = async ({
  analytics,
  runtimeConfig,
  mode,
  isOptedOut,
  isDoNotTrack,
  loadSdk = defaultPostHogLoader,
}: InitializePostHogAnalyticsOptions): Promise<void> => {
  if (
    runtimeConfig.pilotMode ||
    mode !== "production" ||
    !isPublicProjectToken(runtimeConfig.posthogProjectToken) ||
    isOptedOut() ||
    isDoNotTrack()
  ) {
    analytics.disable();
    return;
  }

  try {
    const sdk = await loadSdk();
    if (isOptedOut() || isDoNotTrack()) {
      analytics.disable();
      return;
    }
    sdk.init(runtimeConfig.posthogProjectToken, { ...POSTHOG_CONFIG });
    if (isOptedOut() || isDoNotTrack()) {
      analytics.disable();
      return;
    }
    const transport: AnalyticsTransport = {
      capture: (capture) => {
        try {
          sdk.capture(capture.event, capture.properties);
        } catch {
          // Analytics must never interrupt product behavior.
        }
      },
    };
    analytics.attachTransport(transport);
  } catch {
    analytics.disable();
  }
};
